import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
	CustomField,
	CustomFieldChoice,
	CustomFieldValue,
	PAYER_SIGNATORY_OPTIONS, PAYMENT_SIGNATORY, PLACE_OF_PERFORMANCE,
} from '@app/components/custom-fields/shared/custom-field.model';
import { BaseHttpService } from '@app/services/base-http.service';
import { FormGroup } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class CustomFieldService extends BaseHttpService {

	constructor(private _http: HttpClient) {
		super(_http);
	}

	public getCustomFields(rules: Map<string, string>): Observable<CustomField[]> {
		let params = new HttpParams();
		rules.forEach((value, key) => params = params.append(key, value));
		const request = this.http.get<CustomField[]>(`${environment.config.apiUrl}/custom-fields`, { params });
		return this.typeCastArray(request, CustomField);
	}

	public getProfileCustomFieldValuesUnmasked(profileId : number): Observable<CustomFieldValue[]> {
		const request = this.http.get<CustomFieldValue[]>(`${environment.config.apiUrl}/profiles/${profileId}/custom-field-values-unmasked`);
		return this.typeCastArray(request, CustomFieldValue);
	}

	public getOrderCustomFieldValues(orderId: number): Observable<CustomFieldValue[]> {
		const request = this.http.get<CustomFieldValue[]>(`${environment.config.apiUrl}/orders/${orderId}/custom-field-values`);
		return this.typeCastArray(request, CustomFieldValue);
	}

	public mapToValues(fields: CustomField[], formValues: any): Array<CustomFieldValue> {
		const result: Array<CustomFieldValue> = [];
		if ((! fields || fields?.length <= 0)
		|| (! formValues || Object.keys(formValues)?.length <= 0)) return result;

		fields.forEach(field => {
			const prop = Object.keys(formValues).filter(key => key === field.name)[0];
			let value = formValues[prop];
			let valuePreview = value;
			if (value === '') return;

			if (field.type.alias === 'select'
			|| field.type.alias === 'dynamic_select') {
				let chosen: CustomFieldChoice = null;

				// Using .some we get iteration functionally similar to .forEach but with the ability to break through return instead.
				field.choiceList.some((choice: CustomFieldChoice) => {
					if (choice?.id?.toString()?.toUpperCase() === value?.toString()?.toUpperCase()) {
						// ETT-88 -- Expand the dynamic select for Place of Performance with states list.
						// remove an extra percent value for the Place of Performance choice only
						if (field.name === PLACE_OF_PERFORMANCE) {
							if (choice.name.indexOf('(') !== -1) {
								choice.name = choice.name.substring(0, (choice.name.indexOf('(') - 1));
								chosen = choice;
							}
						}
						chosen = choice;
					} else if (choice.group) {
						// eslint-disable-next-line array-callback-return
						choice.children.some((childChoice: CustomFieldChoice) => {
							// eslint-disable-next-line eqeqeq
							if (childChoice.id == value) {
								chosen = childChoice;
							}
						});
					}

					if (chosen) return true;
					return false;
				});

				if (chosen) {
					value = chosen.id;
					valuePreview = chosen.name;
				}
			} else if (field.type.alias === 'label') {
				value = field.defaultValue;
				valuePreview = field.displayValue;
			}

			result.push({
				values: value?.toString()?.trim(),
				valuePreview: valuePreview?.toString()?.trim(),
				fieldId: field.id,
				fieldLabel: field.label,
				fieldName: field.name,
				fieldTypeAlias: field.type.alias,
				fieldUnmaskedChars: field.unmaskedChars
			});
		});
		return result;
	}

	/** Search for custom field by name */
	public searchCustomField(customFields: Array<CustomField>, name: string): CustomField | undefined {
		return customFields.find((cf: CustomField) => cf.name === name);
	}

	/**
	 * Add/remove a custom field form control based on the display status.
	 *
	 * @param {FormGroup} customFieldsFG - The FormGroup containing the custom fields.
	 * @param {Array<CustomField>} customFields - An array of custom fields.
	 * @param {string} cfName - The name of the custom field to update.
	 * @param {boolean} isDisplayed - A flag indicating whether the custom field form control should be displayed.
	 * @returns {void}
	 *
	 * @see {@link https://netbasal.com/three-ways-to-dynamically-alter-your-form-validation-in-angular-e5fd15f1e946}
	 * @see {@link https://stackoverflow.com/a/49293403/15391077}
	 */
	public setIsDisplayed(customFieldsFG: FormGroup, customFields: Array<CustomField>, cfName: string, isDisplayed: boolean): void {
		const customField: CustomField = this.searchCustomField(customFields, cfName);
		if (customField == null || customFieldsFG == null) return;

		customField.isDisplayed = isDisplayed;
		if (isDisplayed && customField.formControl != null) {
			// const newCF: FormControl = new FormControl(customField.defaultValue, customField.validators);
			customField.formControl.setErrors(null);
			customFieldsFG.addControl(customField.name, customField.formControl);
			// customFieldsFG.updateValueAndValidity({ onlySelf: true });
		} else if (customFieldsFG.get(cfName) != null) {
			customFieldsFG.get(cfName).setErrors(null);
			customFieldsFG.removeControl(customField.name);
			// customFieldsFG.updateValueAndValidity({ onlySelf: true });
		}
	}

	public isCustomSignatory(cfValue: CustomFieldValue): boolean {
		// eslint-disable-next-line no-magic-numbers
		const customSignatoryIds = [3, 4, 5, 7, 10, 11, 12, 13, 14]; // ETT-308 New signatories continues the iteration, so we need to specify ids to be excluded.
		return cfValue.fieldName === PAYER_SIGNATORY_OPTIONS && customSignatoryIds.includes(parseInt(cfValue.values, 10));
	}

	/**
	 * Search for custom field by name.
	 *
	 * @param customFields
	 * @param name     Custom field name to search by.
	 * @param property Custom field property.
	 * @param value    Custom field property value.
	 *
	 * @deprecated
	 */
	protected setCustomFieldProperty(customFields: Array<CustomField>, name: string, property: string, value: any): void {
		const cf: CustomField = this.searchCustomField(customFields, name);
		if (cf != null && Object.prototype.hasOwnProperty.call(cf, property)) cf[property] = value;
	}

	/**
	 * Sort custom fields by a specific property.
	 *
	 * @param {Array<CustomField>} customFields
	 * @param {string} sortBy
	 * @returns {Array<CustomField>}
	 */
	public sortCustomFields(customFields: Array<CustomField>, sortBy: string) {
		return customFields.sort((cfA: CustomField, cfB: CustomField) => {
			if (cfA.name === PAYMENT_SIGNATORY) return -1;
			if (cfB.name === PAYMENT_SIGNATORY) return 1;
			return cfA[sortBy] - cfB[sortBy];
		});
	}
}
